import { useState } from "react";
import { Link } from "react-router-dom";
import { useSalaryCostsByMonth } from "@/features/finances/hooks/useFinances";
import { formatAmount } from "@/utils/formatAmount";
import { useAuthStore } from "@/store/auth";

interface Props {
  projectId: string;
}

const POLISH_MONTHS = [
  "styczeń", "luty", "marzec", "kwiecień", "maj", "czerwiec",
  "lipiec", "sierpień", "wrzesień", "październik", "listopad", "grudzień",
];

export function EmployeeAllocationsReadOnlyPanel({ projectId }: Props) {
  const user = useAuthStore((s) => s.user);
  const isDirector = user?.role === "director";
  const { data: monthRows = [] } = useSalaryCostsByMonth(projectId);
  const [openMonths, setOpenMonths] = useState<Set<string>>(new Set());

  const toggle = (key: string) => {
    setOpenMonths((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const total = monthRows.reduce((s, r) => s + parseFloat(r.amount), 0);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-800 dark:text-gray-200">Płace i alokacje pracowników</h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Widok tylko do odczytu — zarządzanie w panelu Pracownicy</p>
        </div>
        {isDirector && (
          <Link to="/employees" className="text-xs text-[var(--color-primary)] hover:underline font-medium">
            Zarządzaj pracownikami →
          </Link>
        )}
      </div>

      {/* Total */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3">
        <p className="text-xs text-purple-600 font-medium">Łączny koszt płac (od początku projektu)</p>
        <p className="text-xl font-bold text-purple-800">{formatAmount(total.toFixed(2))} PLN</p>
      </div>

      {/* Per-month collapsible */}
      {monthRows.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Brak kosztów płac.</p>
      ) : (
        <div className="rounded-lg overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
          {monthRows.map((row) => {
            const key = `${row.year}-${row.month}`;
            const isOpen = openMonths.has(key);
            const label = `${POLISH_MONTHS[row.month - 1]} ${row.year}`;
            return (
              <div key={key} className="bg-white dark:bg-gray-800">
                <button
                  onClick={() => toggle(key)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-sm"
                >
                  <span className="flex-1 text-gray-700 dark:text-gray-300 font-medium">{label}</span>
                  <span className="font-semibold text-red-700 whitespace-nowrap">
                    -{formatAmount(row.amount)} PLN
                  </span>
                  <span className="text-gray-400 text-xs ml-2">{isOpen ? "▲" : "▼"}</span>
                </button>
                {isOpen && row.items.length > 0 && (
                  <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-100 dark:border-gray-700">
                          <th className="text-left px-5 py-1.5 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Pracownik</th>
                          <th className="text-center px-3 py-1.5 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Alokacja</th>
                          <th className="text-right px-3 py-1.5 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Koszt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {row.items.map((item, i) => (
                          <tr key={i} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                            <td className="px-5 py-1.5 text-gray-600 dark:text-gray-300">{item.employee_name}</td>
                            <td className="px-3 py-1.5 text-center text-gray-500 dark:text-gray-400">
                              {Math.round(parseFloat(item.fraction) * 100)}%
                            </td>
                            <td className="px-3 py-1.5 text-right text-red-600 font-medium whitespace-nowrap">
                              -{formatAmount(item.cost)} PLN
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
